import { useEffect, useState } from "react";
import { namedChainsApi, type NamedChainRow, type NamedChainVersionRow } from "./api/named-chains-client";
import { ConfirmModal } from "@/components/ConfirmModal";

interface VersionHistoryListProps {
  chainId: string;
  onRollback: (updated: NamedChainRow) => void;
}

export function VersionHistoryList({ chainId, onRollback }: VersionHistoryListProps) {
  const [versions, setVersions] = useState<NamedChainVersionRow[] | null>(null);
  const [pendingRollback, setPendingRollback] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    namedChainsApi.versions(chainId).then((data) => {
      if (!cancelled) setVersions(data.versions);
    });
    return () => {
      cancelled = true;
    };
  }, [chainId]);

  function handleRollback(versionId: string) {
    setPendingRollback(versionId);
  }

  async function handleConfirmRollback() {
    if (!pendingRollback) return;
    const versionId = pendingRollback;
    setPendingRollback(null);
    const updated = await namedChainsApi.rollback(chainId, versionId);
    onRollback(updated);
    const fresh = await namedChainsApi.versions(chainId);
    setVersions(fresh.versions);
  }

  if (versions === null) return <div className="admin-loading"><div className="loading-spinner" /></div>;

  if (versions.length === 0) {
    return (
      <div className="rb-section">
        <h2 className="rb-section-title">History Panel</h2>
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            No version history yet — every save that changes the steps array creates a snapshot.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rb-section">
      <h2 className="rb-section-title">History Panel</h2>
      <table>
        <thead>
          <tr>
            <th>Version</th>
            <th>Edited at</th>
            <th>Edited by</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <td>{v.id}</td>
              <td>{new Date(v.editedAt).toLocaleString()}</td>
              <td>{v.editedBy}</td>
              <td>
                <button
                  type="button"
                  className="rb-btn"
                  onClick={() => handleRollback(v.id)}
                >
                  Roll back
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmModal
        open={pendingRollback !== null}
        title="Roll back to this version?"
        description="The current chain will be saved as a new version row."
        confirmLabel="Roll back"
        onConfirm={handleConfirmRollback}
        onCancel={() => setPendingRollback(null)}
      />
    </div>
  );
}
